/**
 * Invoice Status
 *
 * The legacy lifecycle status of a supplier invoice, derived from its `ApprovalStatus`; never
 * stored. History and the AI chat speak it.
 */
import { isDocumentArchivable, type ArchiveGateDocument } from '../utils/document-archive';
import { ApprovalStatus } from './approval-status';
import { ValidationStatus } from './validation-status';

export const InvoiceStatus = {
  /** Being worked on (Rozpracovaná) — approval UNPROCESSED */
  DRAFT: 'DRAFT',
  /** Under approval (Ke schválení) — approval PENDING or FOR_REVIEW */
  FOR_APPROVAL: 'FOR_APPROVAL',
  /** Approved (Schváleno) */
  APPROVED: 'APPROVED',
  /** Refused (Neschváleno) */
  REJECTED: 'REJECTED'
} as const;

export type InvoiceStatus = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

const INVOICE_STATUS_OF: Record<ApprovalStatus, InvoiceStatus> = {
  UNPROCESSED: InvoiceStatus.DRAFT,
  PENDING: InvoiceStatus.FOR_APPROVAL,
  FOR_REVIEW: InvoiceStatus.FOR_APPROVAL,
  APPROVED: InvoiceStatus.APPROVED,
  REJECTED: InvoiceStatus.REJECTED
};

/** The legacy status an approval status is written and shown as. */
export const invoiceStatusOf = (approvalStatus: ApprovalStatus): InvoiceStatus =>
  INVOICE_STATUS_OF[approvalStatus];

/** The approval statuses a legacy status stands for — what a filter on it selects. */
export const invoiceApprovalStatusesOf = (status: InvoiceStatus): ApprovalStatus[] =>
  Object.values(ApprovalStatus).filter(
    approvalStatus => invoiceStatusOf(approvalStatus) === status
  );

/** The approval status a move to a legacy target status lands in; under approval is PENDING. */
export const invoiceApprovalTargetOf = (status: InvoiceStatus): ApprovalStatus => {
  switch (status) {
    case InvoiceStatus.DRAFT:
      return ApprovalStatus.UNPROCESSED;
    case InvoiceStatus.FOR_APPROVAL:
      return ApprovalStatus.PENDING;
    case InvoiceStatus.APPROVED:
      return ApprovalStatus.APPROVED;
    case InvoiceStatus.REJECTED:
      return ApprovalStatus.REJECTED;
  }
};

/**
 * The moves the document actions offer. An approver's direct decision (a batch approve, an AI
 * chat action) is a decision rather than a lifecycle move and is not described here.
 */
export const INVOICE_STATUS_TRANSITIONS: Record<ApprovalStatus, readonly ApprovalStatus[]> = {
  UNPROCESSED: [ApprovalStatus.PENDING],
  PENDING: [
    ApprovalStatus.FOR_REVIEW,
    ApprovalStatus.APPROVED,
    ApprovalStatus.REJECTED,
    ApprovalStatus.UNPROCESSED
  ],
  FOR_REVIEW: [ApprovalStatus.APPROVED, ApprovalStatus.REJECTED, ApprovalStatus.UNPROCESSED],
  // An approval signs the invoice off, so taking it back undoes a decision rather than
  // continuing the work — allowed, but it asks for the approve permission.
  APPROVED: [ApprovalStatus.UNPROCESSED],
  REJECTED: [ApprovalStatus.UNPROCESSED]
};

/** Whether the approval status move is one the lifecycle allows. */
export const isInvoiceStatusTransitionAllowed = (
  from: ApprovalStatus,
  to: ApprovalStatus
): boolean => INVOICE_STATUS_TRANSITIONS[from].includes(to);

/**
 * The invoice content may only change while approval has not started. Everything later is
 * either being decided or already decided, and a change underneath would invalidate it.
 */
export const isInvoiceStatusEditable = (approvalStatus: ApprovalStatus): boolean =>
  approvalStatus === ApprovalStatus.UNPROCESSED;

/**
 * Whether an approval is running or waiting for a user — the only state the automation acts
 * on. A run queued outside it would be discarded, after telling the detail page it was evaluating.
 */
export const isInvoiceUnderApproval = (approvalStatus: ApprovalStatus): boolean =>
  approvalStatus === ApprovalStatus.PENDING || approvalStatus === ApprovalStatus.FOR_REVIEW;

/**
 * Approval statuses an invoice may be dropped from: nothing was ever signed off in them, so
 * deleting destroys no decision. A refused invoice is one nobody wants, so it is one of them.
 */
export const isInvoiceDeletable = (approvalStatus: ApprovalStatus): boolean =>
  approvalStatus === ApprovalStatus.UNPROCESSED || approvalStatus === ApprovalStatus.REJECTED;

/** Whether the invoice may be deleted: never once archived, always once cancelled. */
export const isInvoiceDocumentDeletable = (invoice: {
  readonly approvalStatus?: ApprovalStatus | null;
  readonly cancelledAt?: unknown;
  readonly archivedAt?: unknown;
}): boolean =>
  invoice.archivedAt == null &&
  (invoice.cancelledAt != null ||
    isInvoiceDeletable(invoice.approvalStatus ?? ApprovalStatus.UNPROCESSED));

/** An invoice may be filed away only once completed, never a cancelled one. */
export const isInvoiceArchivable = (invoice: ArchiveGateDocument): boolean =>
  isDocumentArchivable(invoice);

/** Approval statuses an invoice may be cancelled from; cancelling is final and keeps the status. */
export const INVOICE_CANCELLABLE_STATUSES: readonly ApprovalStatus[] = [
  ApprovalStatus.UNPROCESSED,
  ApprovalStatus.REJECTED
];

/** Whether an invoice in this approval status may be cancelled. */
export const isInvoiceCancellable = (approvalStatus: ApprovalStatus): boolean =>
  INVOICE_CANCELLABLE_STATUSES.includes(approvalStatus);

/** An approved invoice is the only one that may be exported or paid. */
export const isInvoiceStatusExportable = (approvalStatus: ApprovalStatus): boolean =>
  approvalStatus === ApprovalStatus.APPROVED;

/**
 * Whether the invoice may be exported: validated cleanly or with its warnings accepted, and not
 * cancelled. Archiving neither grants nor refuses it.
 */
export const isInvoiceExportable = (invoice: {
  readonly validationStatus?: ValidationStatus | null;
  readonly warningsAcceptedAt?: unknown;
  readonly cancelledAt?: unknown;
}): boolean =>
  invoice.cancelledAt == null &&
  (invoice.validationStatus === ValidationStatus.SUCCESS || invoice.warningsAcceptedAt != null);

/** Whether the invoice may be handed over for approval from this approval status. */
export const canSubmitInvoiceForApproval = (approvalStatus: ApprovalStatus): boolean =>
  isInvoiceStatusTransitionAllowed(approvalStatus, ApprovalStatus.PENDING);

/** Whether the invoice may be taken back for editing; under approval this withdraws it. */
export const canReturnInvoiceToDraft = (approvalStatus: ApprovalStatus): boolean =>
  isInvoiceStatusTransitionAllowed(approvalStatus, ApprovalStatus.UNPROCESSED);
